import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ImageField, VideoField } from "@/components/admin/ImageField";
import { CaretDown, CaretUp, Plus, Trash, FloppyDisk } from "@phosphor-icons/react";

const SCHEMA = [
  { key: "seo", title: "SEO / Meta", fields: [
    { k: "title", t: "text", label: "Page Title" },
    { k: "description", t: "textarea", label: "Meta Description" },
    { k: "keywords", t: "textarea", label: "Keywords" },
    { k: "og_image", t: "image", label: "Social Share Image" },
  ]},
  { key: "brand", title: "Brand", fields: [{ k: "name", t: "text", label: "Brand Name" }] },
  { key: "hero", title: "Hero", fields: [
    { k: "label", t: "text", label: "Eyebrow Label" },
    { k: "title_lines", t: "lines", label: "Headline (one line per row)" },
    { k: "subtitle", t: "textarea", label: "Subtitle" },
    { k: "cta_primary", t: "text", label: "Primary Button" },
    { k: "cta_secondary", t: "text", label: "Secondary Button" },
    { k: "background_image", t: "image", label: "Background Image (used as fallback / video poster)" },
    { k: "background_video", t: "video", label: "Background Video (upload mp4/webm, or paste a YouTube/Vimeo/mp4 URL — optional; leave empty to use the image)" },
  ]},
  { key: "about", title: "About the Studio", fields: [
    { k: "label", t: "text" }, { k: "title", t: "textarea" },
    { k: "body1", t: "textarea" }, { k: "body2", t: "textarea" }, { k: "cta", t: "text" },
  ]},
  { key: "founder", title: "Founder", fields: [
    { k: "name", t: "text" }, { k: "role", t: "text" },
    { k: "quote", t: "textarea" }, { k: "image", t: "image" },
    { k: "story", t: "textarea", label: "Story (shown when visitors click 'Read More' — separate paragraphs with a blank line)" },
  ]},
  { key: "manifesto", title: "Manifesto / DNA", fields: [
    { k: "label", t: "text" }, { k: "line1", t: "text" }, { k: "line2", t: "text" },
    { k: "line3", t: "text" }, { k: "subtitle", t: "text" }, { k: "image", t: "image", label: "Side Image" },
  ]},
  { key: "process", title: "Process", fields: [
    { k: "label", t: "text" }, { k: "title", t: "text" }, { k: "body", t: "textarea" },
    { k: "steps", t: "list", item: [{ k: "num", t: "text" }, { k: "title", t: "text" }, { k: "desc", t: "textarea" }, { k: "image", t: "image" }] },
  ]},
  { key: "materials", title: "Material Library", fields: [
    { k: "label", t: "text" }, { k: "title", t: "text" }, { k: "body", t: "textarea" },
    { k: "items", t: "list", item: [{ k: "num", t: "text" }, { k: "title", t: "text" }, { k: "desc", t: "textarea" }, { k: "image", t: "image" }] },
  ]},
  { key: "journal", title: "Studio Journal / Process", fields: [
    { k: "label", t: "text" }, { k: "title", t: "text" }, { k: "emphasis", t: "text", label: "Emphasis (italic line)" }, { k: "body", t: "textarea" },
    { k: "projects", t: "list", label: "Process Projects", item: [
      { k: "name", t: "text", label: "Project Name" },
      { k: "location", t: "text", label: "Location / Subtitle" },
      { k: "rooms", t: "list", label: "Rooms (visitors switch between rooms — each holds up to 3 after + 3 before shots)", item: [
        { k: "name", t: "text", label: "Room Name (e.g. Living Room)" },
        { k: "after1", t: "image", label: "After — Shot 1 (shown first)" },
        { k: "after2", t: "image", label: "After — Shot 2" },
        { k: "after3", t: "image", label: "After — Shot 3" },
        { k: "before1", t: "image", label: "Before — Shot 1 (original / raw space)" },
        { k: "before2", t: "image", label: "Before — Shot 2" },
        { k: "before3", t: "image", label: "Before — Shot 3" },
      ] },
    ] },
  ]},
  { key: "marquee", title: "Marquee Ribbon", fields: [{ k: "text", t: "text" }] },
  { key: "social", title: "Social & Contact", fields: [
    { k: "email", t: "text" }, { k: "instagram", t: "text" }, { k: "whatsapp", t: "text" }, { k: "phone", t: "text" },
  ]},
  { key: "closing", title: "Closing CTA", fields: [
    { k: "label", t: "text" }, { k: "title", t: "textarea" }, { k: "emphasis", t: "text" }, { k: "body", t: "textarea" }, { k: "cta", t: "text" }, { k: "background_image", t: "image" },
  ]},
  { key: "footer", title: "Footer", fields: [
    { k: "tagline", t: "textarea" }, { k: "email", t: "text" }, { k: "instagram", t: "text" }, { k: "location", t: "text" },
  ]},
];

export default function ContentPanel() {
  const [content, setContent] = useState(null);
  const [open, setOpen] = useState("seo");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then(({ data }) => setContent(data || {}));
  }, []);

  const setField = (section, key, value) => {
    setContent((c) => ({ ...c, [section]: { ...(c[section] || {}), [key]: value } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", content);
      toast.success("Content saved — refresh the site to see changes");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!content) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="max-w-3xl pb-24">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-3xl font-light">Content</h2>
          <p className="text-sm text-muted-foreground mt-1">Every word and image on the site. Nothing is hardcoded.</p>
        </div>
        <button data-testid="save-content" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-foreground text-background text-xs uppercase tracking-[0.15em] px-5 py-3 hover:bg-accent transition-colors disabled:opacity-50">
          <FloppyDisk size={16} /> {saving ? "Saving…" : "Save All"}
        </button>
      </div>

      <div className="space-y-3">
        {SCHEMA.map((section) => (
          <div key={section.key} className="border border-border bg-card">
            <button
              data-testid={`section-${section.key}`}
              onClick={() => setOpen(open === section.key ? "" : section.key)}
              className="w-full flex items-center justify-between px-5 py-4"
            >
              <span className="text-sm uppercase tracking-[0.15em]">{section.title}</span>
              <CaretDown size={16} className={`transition-transform ${open === section.key ? "rotate-180" : ""}`} />
            </button>
            {open === section.key && (
              <div className="px-5 pb-6 space-y-5 border-t border-border pt-5">
                {section.fields.map((f) => (
                  <FieldRenderer key={f.k} field={f} value={content[section.key]?.[f.k]} onChange={(v) => setField(section.key, f.k, v)} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Primitive({ field, value, onChange }) {
  const label = field.label || field.k.replace(/_/g, " ");
  if (field.t === "image") return <ImageField label={label} value={value} onChange={onChange} />;
  if (field.t === "video") return <VideoField label={label} value={value} onChange={onChange} />;
  if (field.t === "textarea")
    return (
      <Wrap label={label}>
        <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} />
      </Wrap>
    );
  if (field.t === "lines")
    return (
      <Wrap label={label}>
        <textarea rows={3} value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} className={cls} />
      </Wrap>
    );
  return (
    <Wrap label={label}>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} />
    </Wrap>
  );
}

function ListEditor({ field, value, onChange, label }) {
  const update = (i, k, v) => {
    const next = value.map((item, idx) => (idx === i ? { ...item, [k]: v } : item));
    onChange(next);
  };
  const add = () => onChange([...value, {}]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground capitalize">{label}</label>
        <button onClick={add} className="inline-flex items-center gap-1 text-xs text-accent"><Plus size={14} /> Add</button>
      </div>
      <div className="space-y-4">
        {value.map((item, i) => (
          <div key={i} className="border border-border p-4 space-y-3 relative">
            <div className="absolute top-3 right-3 flex items-center gap-2">
              <button
                data-testid={`list-move-up-${i}`}
                onClick={() => move(i, -1)}
                disabled={i === 0}
                aria-label="Move up"
                className="text-muted-foreground hover:text-foreground disabled:opacity-25 transition-colors"
              >
                <CaretUp size={14} />
              </button>
              <button
                data-testid={`list-move-down-${i}`}
                onClick={() => move(i, 1)}
                disabled={i === value.length - 1}
                aria-label="Move down"
                className="text-muted-foreground hover:text-foreground disabled:opacity-25 transition-colors"
              >
                <CaretDown size={14} />
              </button>
              <button onClick={() => remove(i)} aria-label="Remove" className="text-destructive"><Trash size={14} /></button>
            </div>
            {field.item.map((sub) =>
              React.createElement(FieldRenderer, { key: sub.k, field: sub, value: item[sub.k], onChange: (v) => update(i, sub.k, v) })
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange }) {
  const label = field.label || field.k.replace(/_/g, " ");
  if (field.t === "list") return <ListEditor field={field} value={value || []} onChange={onChange} label={label} />;
  return <Primitive field={field} value={value} onChange={onChange} />;
}

const cls = "w-full bg-transparent border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground transition-colors";
function Wrap({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2 capitalize">{label}</label>
      {children}
    </div>
  );
}
